import { HttpErrorResponse } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    AvailableSmartAssistantImportDTO,
    CompanySmartAssistantManagementControllerService,
    SmartAssistantColumnMappingDTO,
    SmartAssistantDateFormat,
    SmartAssistantImportSummaryDTO,
    SmartAssistantType,
    SolutionPartnerSmartAssistantControllerService,
    UrbanSportsClubMembershipStatusImportRequestDTO
} from '@iota/inh-core-v1-management-client';
import { MAT_DIALOG_DATA } from '@iota/iota-ui-components';
import { Observable } from 'rxjs';
import { SmartAssistantService } from 'src/app/administration/business-partners/details/smart-assistant/smart-assistant.service';
import {
    SMART_ASSISTANT_STEP_LABELS,
    SMART_ASSISTANT_TYPE_CONFIGS,
    SmartAssistantStep,
    SmartAssistantValidationResult,
    toLabeledColumns
} from 'src/app/shared/constants-and-enums/smart-assistant-types';
import { ProductListService } from 'src/app/shared/services/product-list.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import {
    forgetStoredColumnMapping,
    rememberColumnHeader,
    resolveStoredColumnMapping
} from 'src/app/shared/utils/smart-assistant-column-mapping.util';
import {
    canProceed,
    hasAmbiguousDateFormat,
    hasNoChanges,
    SmartAssistantImportState,
    toApplyErrorMessage,
    toColumnRows,
    toDateFormatsDTO,
    toEnteredColumnMapping,
    toExplanationItems,
    toIdentificationNumberImportRequest,
    toInitialImportState,
    toProductOptions,
    toProductStepStatus,
    toResultPage,
    toValidatedProductName,
    withValidatedDateFormats
} from './smart-assistant-import.mapper';

@Injectable()
export class SmartAssistantImportService {
    private readonly page = inject(SmartAssistantService);
    private readonly companyApi = inject(CompanySmartAssistantManagementControllerService);
    private readonly partnerApi = inject(SolutionPartnerSmartAssistantControllerService);
    private readonly productList = inject(ProductListService);
    private readonly toastService = inject(ToastService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly state = signal<SmartAssistantImportState>(
        toInitialImportState(inject<AvailableSmartAssistantImportDTO>(MAT_DIALOG_DATA))
    );

    readonly companyId = this.page.companyId;
    private readonly partnerProducts = this.productList.byVendorId(this.companyId()!);

    private readonly steps = computed(() => this.state().steps);
    readonly currentStepIndex = computed(() => this.state().currentStepIndex);
    private readonly currentStep = computed(() => this.steps()[this.currentStepIndex()]);
    readonly stepLabels = computed(() => this.steps().map((step) => SMART_ASSISTANT_STEP_LABELS[step]));

    readonly importType = computed(() => this.state().importType);
    readonly typeConfig = computed(() => SMART_ASSISTANT_TYPE_CONFIGS[this.importType()]);
    readonly resultPage = computed(() => toResultPage(this.importType()));

    private readonly requiredColumns = computed(() => this.state().requiredColumns);
    private readonly optionalColumns = computed(() => this.state().optionalColumns);
    readonly importColumns = computed(() => [...this.requiredColumns(), ...this.optionalColumns()]);
    private readonly allowedValues = computed(() => this.state().allowedValues);
    readonly columnLabels = computed(() => this.state().columnLabels);
    private readonly dateFormats = computed(() => this.state().dateFormats);
    private readonly requiredLabeledColumns = computed(() => toLabeledColumns(this.requiredColumns(), this.columnLabels()));
    private readonly optionalLabeledColumns = computed(() => toLabeledColumns(this.optionalColumns(), this.columnLabels()));
    readonly requiredColumnRows = computed(() => toColumnRows(this.requiredLabeledColumns(), this.importType(), this.dateFormats()));
    readonly optionalColumnRows = computed(() => toColumnRows(this.optionalLabeledColumns(), this.importType(), this.dateFormats()));
    readonly requiredColumnLabels = computed(() => this.requiredLabeledColumns().map((column) => column.label));
    readonly optionalColumnLabels = computed(() => this.optionalLabeledColumns().map((column) => column.label));
    readonly explanationItems = computed(() =>
        toExplanationItems(this.importType(), this.requiredColumns(), this.optionalColumns(), this.allowedValues(), this.columnLabels())
    );

    readonly fileHeaders = computed(() => this.state().fileHeaders);
    readonly fileError = computed(() => this.state().fileError);
    readonly columnMapping = computed(() => this.state().columnMapping);
    private readonly storedColumnMapping = signal(resolveStoredColumnMapping());
    readonly hasStoredColumnMapping = computed(() => Object.keys(this.storedColumnMapping()).length > 0);
    readonly urbanSportsClubTargetMonth = computed(() => this.state().urbanSportsClubTargetMonth);
    readonly selectedProductId = computed(() => this.state().productId);
    readonly productOptions = computed(() => toProductOptions(this.partnerProducts()));
    readonly productStepStatus = computed(() =>
        toProductStepStatus(this.productList.loading(), this.productList.error(), this.productOptions().length)
    );

    readonly validationResult = computed(() => this.state().validationResult);
    readonly validatedProductName = computed(() => toValidatedProductName(this.validationResult()));
    readonly validationLoading = computed(() => this.state().validationLoading);
    readonly hasAmbiguousDateFormat = computed(() => hasAmbiguousDateFormat(this.validationResult()));
    readonly hasErrors = computed(() => (this.validationResult()?.errorEntries ?? 0) > 0);
    readonly hasNoChanges = computed(() => hasNoChanges(this.validationResult()));

    readonly applyLoading = computed(() => this.state().applyLoading);
    readonly apiError = computed(() => this.state().apiError);
    readonly applyErrorMessage = computed(() => toApplyErrorMessage(this.state().applyDataChanged));

    readonly isInFirstStep = computed(() => this.currentStepIndex() === 0);
    readonly isInProductStep = computed(() => this.currentStep() === SmartAssistantStep.PRODUCT);
    readonly isInUrbanSportsClubDateStep = computed(() => this.currentStep() === SmartAssistantStep.URBAN_SPORTS_CLUB_DATE);
    readonly isInUploadStep = computed(() => this.currentStep() === SmartAssistantStep.UPLOAD_FILE);
    readonly isInAssignColumnsStep = computed(() => this.currentStep() === SmartAssistantStep.ASSIGN_COLUMNS);
    readonly isInValidateDataStep = computed(() => this.currentStep() === SmartAssistantStep.VALIDATE_DATA);
    readonly isInCompletionStep = computed(() => this.currentStep() === SmartAssistantStep.COMPLETION);

    readonly isBusy = computed(() => this.validationLoading() || this.applyLoading());
    readonly nextButtonDisabled = computed(() => !canProceed(this.state()) || this.state().cancelLoading || this.isBusy());

    constructor() {
        if (this.steps().includes(SmartAssistantStep.PRODUCT)) {
            this.productList.load();
        }
    }

    updateFileHeaders(headers: string[]): void {
        this.state.update((state) => ({ ...state, fileHeaders: headers, fileError: null, dateFormats: {} }));
    }

    updateFileError(fileError: string): void {
        this.state.update((state) => ({ ...state, fileError, fileHeaders: null, dateFormats: {} }));
    }

    updateColumnMapping(key: string, header: string): void {
        this.state.update((state) => {
            const columnMapping = { ...state.columnMapping, [key]: header };
            if (!header) {
                delete columnMapping[key];
            }
            const manualColumns = state.manualColumns.includes(key) ? state.manualColumns : [...state.manualColumns, key];
            return { ...state, columnMapping, manualColumns };
        });
        this.storedColumnMapping.set(rememberColumnHeader(key, header));
    }

    forgetStoredColumnMapping(): void {
        forgetStoredColumnMapping();
        this.storedColumnMapping.set({});
        this.toastService.showSuccess($localize`Saved column assignments have been deleted.`);
    }

    updateDateFormat(key: string, format: SmartAssistantDateFormat | null): void {
        this.state.update((state) => {
            const dateFormats = { ...state.dateFormats, [key]: format };
            if (!format) {
                delete dateFormats[key];
            }
            return { ...state, dateFormats };
        });
    }

    updateUrbanSportsClubTargetMonth(year: number, month: UrbanSportsClubMembershipStatusImportRequestDTO.MonthEnum): void {
        this.state.update((state) => ({ ...state, urbanSportsClubTargetMonth: { year, month } }));
    }

    updateProductId(productId: string | null): void {
        this.state.update((state) => ({ ...state, productId }));
    }

    reloadProducts(): void {
        this.productList.reload();
    }

    nextStep(file: File | null): void {
        if (this.isInProductStep() || this.isInUrbanSportsClubDateStep() || this.isInUploadStep()) {
            const entersColumnMapping = this.isInUploadStep();
            this.state.update((state) => ({
                ...state,
                ...(entersColumnMapping ? toEnteredColumnMapping(state, this.storedColumnMapping()) : {}),
                currentStepIndex: Math.min(state.currentStepIndex + 1, state.steps.length - 1)
            }));
            return;
        }

        if (this.isInAssignColumnsStep()) {
            this.validateImport(file);
            return;
        }

        if (this.isInValidateDataStep()) {
            this.applyImport();
        }
    }

    previousStep(): void {
        if (this.state().currentImportId) {
            this.cancelImport();
        }
        this.state.update((state) => ({ ...state, currentStepIndex: Math.max(state.currentStepIndex - 1, 0) }));
    }

    cancelUnfinishedImport(): void {
        if (!this.isInCompletionStep() && this.state().currentImportId) {
            this.cancelImport();
        }
    }

    downloadReport(): void {
        const importId = this.state().currentImportId;
        if (importId) {
            this.page.downloadReport(importId);
        }
    }

    private validateImport(file: File): void {
        this.state.update((state) => ({ ...state, validationLoading: true, apiError: false }));

        const validation$ = this.page.owner === 'COMPANY' ? this.validateCompanyImport(file) : this.validatePartnerImport(file);
        if (!validation$) {
            this.failValidation();
            return;
        }

        validation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (result) =>
                this.state.update((state) => {
                    const validationStepIndex = state.steps.indexOf(SmartAssistantStep.VALIDATE_DATA);
                    return {
                        ...state,
                        validationResult: result,
                        dateFormats: withValidatedDateFormats(state.dateFormats, result),
                        currentImportId: result.id ?? null,
                        validationLoading: false,
                        currentStepIndex: validationStepIndex >= 0 ? validationStepIndex : state.currentStepIndex + 1
                    };
                }),
            error: (error: HttpErrorResponse) => {
                this.toastService.showError(
                    error?.status === 413
                        ? $localize`The file is too large. The maximum size is 50 MB.`
                        : $localize`The file could not be validated. Please check the file and the column assignment.`
                );
                this.failValidation();
            }
        });
    }

    private failValidation(): void {
        this.state.update((state) => ({ ...state, validationLoading: false, apiError: true }));
    }

    private validateCompanyImport(file: File): Observable<SmartAssistantValidationResult> | null {
        const { importType, columnMapping, dateFormats } = this.state();
        const companyId = this.companyId()!;
        const columnMappingDTO: SmartAssistantColumnMappingDTO = { columnMapping };
        switch (importType) {
            case SmartAssistantType.CHANGING_BUDGET:
                return this.companyApi.validateChangingBudget(companyId, columnMappingDTO, file);
            case SmartAssistantType.CHANGING_EMPLOYEE_GROUP:
                return this.companyApi.validateChangingEmployeeGroup(companyId, columnMappingDTO, file);
            case SmartAssistantType.CHANGING_REGISTERED_EMPLOYEE_GROUP:
                return this.companyApi.validateChangingRegisteredEmployeeGroup(
                    companyId,
                    columnMappingDTO,
                    file,
                    toDateFormatsDTO(columnMapping, dateFormats)
                );
            case SmartAssistantType.CHANGING_OFFICE:
                return this.companyApi.validateChangingOffice(companyId, columnMappingDTO, file);
            case SmartAssistantType.LEAVING_EMPLOYEES:
                return this.companyApi.validateLeavingEmployees(
                    companyId,
                    columnMappingDTO,
                    file,
                    toDateFormatsDTO(columnMapping, dateFormats)
                );
            case SmartAssistantType.NEW_EMPLOYEES:
                return this.companyApi.validateNewEmployees(
                    companyId,
                    columnMappingDTO,
                    file,
                    toDateFormatsDTO(columnMapping, dateFormats)
                );
            case SmartAssistantType.CHANGING_TIME_IN_LIEU:
                return this.companyApi.validateChangingTimeInLieu(companyId, columnMappingDTO, file);
            case SmartAssistantType.CUSTOMER_REPORTING_DATA:
                return this.companyApi.validateCustomerReportingData(companyId, columnMappingDTO, file);
            default:
                this.toastService.showError($localize`Unknown import type`);
                return null;
        }
    }

    private validatePartnerImport(file: File): Observable<SmartAssistantValidationResult> | null {
        const { importType, columnMapping, urbanSportsClubTargetMonth, productId } = this.state();
        const companyId = this.companyId()!;
        switch (importType) {
            case SmartAssistantType.EMPLOYEE_SOLUTION_PARTNER_IDENTIFICATION_NUMBER_IMPORT:
                return this.partnerApi.validateIdentificationNumbers(
                    companyId,
                    file,
                    toIdentificationNumberImportRequest(columnMapping, productId)
                );
            case SmartAssistantType.URBAN_SPORTS_CLUB_MEMBERSHIP_STATUS_IMPORT:
                if (!urbanSportsClubTargetMonth) {
                    this.toastService.showError($localize`Please select a month and year first`);
                    return null;
                }
                return this.partnerApi.validateUrbanSportsClubMembershipStatus(companyId, file, {
                    columnMapping,
                    month: urbanSportsClubTargetMonth.month,
                    year: urbanSportsClubTargetMonth.year
                });
            default:
                this.toastService.showError($localize`Unknown import type`);
                return null;
        }
    }

    private applyImport(): void {
        const { currentImportId } = this.state();
        const companyId = this.companyId()!;
        this.state.update((state) => ({ ...state, applyLoading: true, applyDataChanged: false, apiError: false }));

        this.applyImportRequest(companyId, currentImportId).subscribe({
            next: (result) => {
                this.state.update((state) => {
                    const completionStepIndex = state.steps.indexOf(SmartAssistantStep.COMPLETION);
                    return {
                        ...state,
                        applyResult: result,
                        applyLoading: false,
                        currentStepIndex: completionStepIndex >= 0 ? completionStepIndex : state.currentStepIndex + 1
                    };
                });
                this.page.reloadLatestImports();
            },
            error: (error: HttpErrorResponse) => {
                this.state.update((state) => {
                    const completionStepIndex = state.steps.indexOf(SmartAssistantStep.COMPLETION);
                    return {
                        ...state,
                        applyLoading: false,
                        applyDataChanged: error?.status === 409,
                        apiError: true,
                        currentStepIndex: completionStepIndex >= 0 ? completionStepIndex : state.currentStepIndex
                    };
                });
                this.page.reloadLatestImports();
            }
        });
    }

    private cancelImport(): void {
        const { currentImportId, validationResult } = this.state();
        this.state.update((state) => ({ ...state, cancelLoading: true }));

        if (!currentImportId || validationResult?.canBeCancelled === false) {
            this.clearCancelledImport();
            return;
        }

        this.cancelImportRequest(this.companyId()!, currentImportId).subscribe({
            next: () => {
                this.clearCancelledImport();
                this.page.reloadLatestImports();
            },
            error: () => this.clearCancelledImport()
        });
    }

    private clearCancelledImport(): void {
        this.state.update((state) => ({ ...state, currentImportId: null, validationResult: null, cancelLoading: false }));
    }

    private applyImportRequest(companyId: string, importId: string): Observable<SmartAssistantImportSummaryDTO> {
        return this.page.owner === 'COMPANY'
            ? this.companyApi.applyImport(companyId, importId)
            : this.partnerApi.applyImport(companyId, importId);
    }

    private cancelImportRequest(companyId: string, importId: string): Observable<SmartAssistantImportSummaryDTO> {
        return this.page.owner === 'COMPANY'
            ? this.companyApi.cancelImport(companyId, importId)
            : this.partnerApi.cancelImport(companyId, importId);
    }
}
